/** Pluggable parcel provider (§3.4): one interface, Maricopa first.
 *  Outside coverage the answer is a typed no-coverage result that routes the UI
 *  to manual boundary drawing — a complete path, not a dead end. */
import { z } from 'zod';
import { fetchJson, ServiceError } from '../core/net';
import { netRingsAreaSqFt, perimeterFt, type LatLng } from '../core/geo';
import type { ParcelResult } from '../core/types';
import { nowIso } from '../core/ids';

export type ParcelLookup =
  | { ok: true; parcel: ParcelResult }
  | { ok: false; reason: 'no_coverage' | 'no_parcel'; message: string };

export interface ParcelProvider {
  key: string;
  label: string;
  covers(lat: number, lng: number): boolean;
  lookup(lat: number, lng: number): Promise<ParcelLookup>;
}

const EsriParcelSchema = z.object({
  features: z.array(z.object({
    attributes: z.object({
      APN: z.string().nullish(),
      PHYSICAL_ADDRESS: z.string().nullish(),
      SUBNAME: z.string().nullish(),
      LOT_NUM: z.union([z.string(), z.number()]).nullish(),
      PUC: z.string().nullish(),
      OWNER_NAME: z.string().nullish(),
    }).loose(),
    geometry: z.object({ rings: z.array(z.array(z.tuple([z.number(), z.number()]).rest(z.number()))) }).nullish(),
  })),
}).loose();

/** Maricopa County Assessor public GIS (§12.2). Rings arrive [lng, lat]; we store [lat, lng]. */
export const maricopaProvider: ParcelProvider = {
  key: 'maricopa',
  label: 'Maricopa County',
  covers(lat, lng) {
    return lat >= 32.45 && lat <= 34.08 && lng >= -113.40 && lng <= -111.00;
  },
  async lookup(lat, lng) {
    const params = new URLSearchParams({
      f: 'json',
      geometry: JSON.stringify({ x: lng, y: lat, spatialReference: { wkid: 4326 } }),
      geometryType: 'esriGeometryPoint',
      spatialRel: 'esriSpatialRelIntersects',
      inSR: '4326', outSR: '4326',
      outFields: 'APN,PHYSICAL_ADDRESS,SUBNAME,LOT_NUM,PUC,OWNER_NAME',
      returnGeometry: 'true',
    });
    const raw = await fetchJson('Maricopa County GIS',
      `https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Parcels/MapServer/0/query?${params.toString()}`);
    const parsed = EsriParcelSchema.safeParse(raw);
    if (!parsed.success) throw new ServiceError('Maricopa County GIS', 'malformed', 'The county answered in an unexpected shape.');
    const f = parsed.data.features[0];
    if (!f || !f.geometry?.rings?.length) {
      return { ok: false, reason: 'no_parcel', message: 'The county has no parcel at that point. Check the pin position, or draw the boundary by hand.' };
    }
    const rings: LatLng[][] = f.geometry.rings.map((ring) => ring.map((pt) => [pt[1], pt[0]] as LatLng));
    return {
      ok: true,
      parcel: {
        provider: 'maricopa',
        apn: f.attributes.APN ?? '',
        address: f.attributes.PHYSICAL_ADDRESS ?? undefined,
        subname: f.attributes.SUBNAME?.trim() || undefined,
        lotNum: f.attributes.LOT_NUM != null ? String(f.attributes.LOT_NUM).trim() : undefined,
        puc: f.attributes.PUC?.trim() || undefined,
        owner: f.attributes.OWNER_NAME?.trim() || undefined,
        rings,
        lotSqFt: Math.round(netRingsAreaSqFt(rings)),
        perimeterFt: Math.round(perimeterFt(rings[0] ?? [])),
        fetchedAt: nowIso(),
      },
    };
  },
};

/** A recorded lot inside a subdivision — i.e. somebody's house, not the community.
 *  Loading one of these silently is the 2026-07-25 field defect: the app outlined a
 *  single residence inside an HOA and said nothing about it. Both the search bar and
 *  the tap-the-map path check this before reporting what they found. */
export function looksLikeOneHouse(parcel: Pick<ParcelResult, 'lotSqFt' | 'subname'>): boolean {
  return !!parcel.subname && parcel.lotSqFt > 0 && parcel.lotSqFt < 20000;
}

const PROVIDERS: ParcelProvider[] = [maricopaProvider];

/** Adding Pinal County later = writing one more provider and appending it here. */
export async function lookupParcel(lat: number, lng: number): Promise<ParcelLookup> {
  const p = PROVIDERS.find((x) => x.covers(lat, lng));
  if (!p) {
    return {
      ok: false, reason: 'no_coverage',
      message: 'Parcel data is not available here yet — only Maricopa County is connected. Draw the property boundary by hand instead; everything else works the same.',
    };
  }
  return p.lookup(lat, lng);
}
